// react
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// react-aria
import { useTextField } from 'react-aria';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '../Icon';

interface InputProps extends FieldProps<string> {
  className?: string;
  size?: 'sm' | 'md';
  iconLeft?: Icons;
  iconRight?: Icons;
  type?: 'password' | 'email' | 'text' | 'number';
  autoFocus?: boolean;
  minLength?: number;
  maxLength?: number;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  isCentered?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Input allows users to enter and edit text.
 */
export const Input = ({
  id = 'input',
  className,
  label,
  description,
  iconLeft,
  iconRight,
  size = 'md',
  type = 'text',
  placeholder,
  ariaLabel,
  tooltip,
  value,
  onChange,
  validate = () => true,
  isDisabled = false,
  isRequired = false,
  isHideTitle = false,
  autoFocus = false,
  minLength,
  maxLength,
  onClick,
  isCentered,
  onKeyDown,
  ...props
}: InputProps) => {
  // state
  const [isError, setIsError] = useState(false);

  // hooks/
  const ref = useRef<HTMLInputElement>(null);
  let { inputProps } = useTextField(
    {
      ...props,
      isDisabled,
      placeholder,
      id,
      type,
      autoFocus,
      minLength: minLength,
      maxLength: maxLength,
      onKeyDown,
      'aria-label': ariaLabel || label,
    },
    ref,
  );

  // effects
  useEffect(() => {
    setIsError(!validate(value) && (value?.length || 0) > 0);
  }, [value, validate]);

  return (
    <BaseField
      id={id}
      ariaLabel={ariaLabel}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <StyledInputContainer
        className={className}
        $size={size}
        $isError={isError}
        $isDisabled={isDisabled}
      >
        {iconLeft && (
          <StyledIcon size={size}>
            <Icon icon={iconLeft} fontSize={size === 'sm' ? 12 : 16} />
          </StyledIcon>
        )}
        <StyledInput
          {...inputProps}
          ref={ref}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onClick={onClick}
          aria-label={ariaLabel}
          $inputSize={size}
          $isCentered={isCentered}
        />
        {!isError && iconRight && (
          <StyledIcon size={size} isRight>
            <Icon icon={iconRight} fontSize={size === 'sm' ? 12 : 16} />
          </StyledIcon>
        )}
        {isError && (
          <StyledErrorIcon size={size}>
            <Icon
              icon='exclamation-circle'
              fontSize={size === 'sm' ? 12 : 16}
            />
          </StyledErrorIcon>
        )}
      </StyledInputContainer>
    </BaseField>
  );
};

interface StyledInputContainerProps {
  $size?: 'sm' | 'md';
  $isDisabled?: boolean;
  $isError?: boolean;
}

const StyledInputContainer = styled.div<StyledInputContainerProps>`
  // default styles
  display: inline-flex;
  align-items: center;
  width: 100%;

  color: ${(props) => props.theme.color.neutral[90]};
  background: ${(props) => props.theme.color.neutral[10]};
  padding: 0;
  border: 1px solid ${(props) => props.theme.color.neutral[60]};
  border-radius: ${(props) => (props.$size === 'sm' ? '4px' : '8px')};

  &:focus-within {
    outline: 3px solid ${(props) => props.theme.color.primary[20]};
    border: 1px solid ${(props) => props.theme.color.primary[50]};
  }

  // error styles
  ${(props) =>
    props.$isError &&
    css`
      border: 1px solid ${props.theme.color.danger[40]};
      background: ${props.theme.color.neutral[30]};
      &:focus-within {
        border: 1px solid ${props.theme.color.danger[40]};
      }
    `}

  // disabled styles
  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[30]};
      color: ${props.theme.color.neutral[50]};
      border: 1px solid ${props.theme.color.neutral[50]};
    `}
`;

interface StyledInputProps {
  $inputSize?: 'sm' | 'md';
  $isCentered?: boolean;
}

const StyledInput = styled.input<StyledInputProps>`
  min-width: 0;
  width: 100%;
  color: inherit;
  border: none;
  outline: none;
  background: none;
  margin: 0;
  caret-color: ${(props) => props.theme.color.primary[50]};
  text-align: ${(props) => (props.$isCentered ? 'center' : 'left')};

  ${(props) => {
    switch (props.$inputSize) {
      case 'sm':
        return css`
          font: ${props.theme.typography.body.sm.regular};
          padding: ${props.theme.spacing[4]} ${props.theme.spacing[12]};
        `;
      case 'md':
        return css`
          font: ${props.theme.typography.body.md.medium};
          padding: ${props.theme.spacing[12]};
        `;
    }
  }}

  &::placeholder {
    color: ${(props) => props.theme.color.neutral[60]};
  }
`;

interface StyledIconProps {
  size?: 'sm' | 'md';
  isRight?: boolean;
}

const StyledIcon = styled.div<StyledIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: ${(props) => props.theme.color.neutral[90]};

  ${(props) => {
    switch (props.size) {
      case 'sm':
        return css`
          padding: ${props.theme.spacing[4]} ${props.theme.spacing[12]};
        `;
      case 'md':
        return css`
          padding: ${props.theme.spacing[12]};
        `;
    }
  }}
  ${(props) =>
    props.isRight
      ? css`
          padding-left: 0;
        `
      : css`
          padding-right: 0;
        `}
`;

interface StyledErrorIconProps {
  size?: 'sm' | 'md';
}

const StyledErrorIcon = styled.div<StyledErrorIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: ${(props) => props.theme.color.danger[40]};
  ${(props) => {
    switch (props.size) {
      case 'sm':
        return css`
          padding: ${props.theme.spacing[4]} ${props.theme.spacing[12]};
        `;
      case 'md':
        return css`
          padding: ${props.theme.spacing[12]};
        `;
    }
  }}
  padding-left: 0;
`;

Input.componentName = 'Input';

export default Input;
